import React, { useEffect, useState } from 'react';

import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import Cookies from 'js-cookie';

const COOKIE_PAYMENT_FAILED_STATE_KEY = 'htcPaymentFailedConfig';
const SHOW_INTERVAL = 2 * 60 * 60 * 1000; // two hours
const CHECK_INTERVAL = 5 * 60 * 1000; // five minutes
const CAN_CLOSE_DURING = 6 * 24 * 60 * 60 * 1000; // 6 days
const CAN_DOWNGRADE_DURING = 29 * 24 * 60 * 60 * 1000; // 29 days

type ModalConfig = {
  shouldShow: boolean;
  canClose: boolean;
  currentTime: Date;
  firstShown: Date;
  closeVariation: 'close' | 'downgrade' | null;
};

export const useModalControl = (): ModalConfig & {
  triggerUpdate: () => void;
} => {
  const currentSubscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );

  const [modalConfig, setModalConfig] = useState<ModalConfig>({
    shouldShow: false,
    canClose: false,
    currentTime: new Date(),
    firstShown: new Date(),
    closeVariation: null,
  });

  const evaluateConditions = React.useCallback((): void => {
    const currentTime = new Date();
    const modalData = JSON.parse(
      decodeURIComponent(Cookies.get(COOKIE_PAYMENT_FAILED_STATE_KEY) ?? 'null')
    );
    const lastShown = modalData ? new Date(modalData.lastShown) : null;
    const firstShown = modalData ? new Date(modalData.firstShown) : null;
    const shownSince = lastShown
      ? currentTime.getTime() - lastShown.getTime()
      : 0;
    const dueSince = firstShown
      ? currentTime.getTime() - firstShown.getTime()
      : 0;
    // Determine if modal can be shown
    const shouldShowCookie = !lastShown || shownSince >= SHOW_INTERVAL;

    let shouldShow = Boolean(shouldShowCookie && currentSubscription?.pastDue);

    // Determine if modal can close
    let canClose = false;
    let closeVariation: ModalConfig['closeVariation'] = null;

    if (!lastShown || dueSince < CAN_CLOSE_DURING) {
      canClose = true;
      closeVariation = 'close';
    } else if (dueSince < CAN_DOWNGRADE_DURING) {
      canClose = true;
      closeVariation = 'downgrade';
    }

    // show on every on load after the exception period
    if (dueSince >= CAN_DOWNGRADE_DURING) {
      shouldShow = true;
    }

    setModalConfig(() => ({
      shouldShow,
      canClose,
      currentTime,
      firstShown: firstShown ?? currentTime,
      closeVariation: closeVariation,
    }));
  }, [setModalConfig, currentSubscription?.pastDue]);

  useEffect(() => {
    if (modalConfig.shouldShow) {
      Cookies.set(
        COOKIE_PAYMENT_FAILED_STATE_KEY,
        JSON.stringify({
          lastShown: modalConfig.currentTime.toISOString(),
          firstShown: modalConfig.firstShown.toISOString(),
        }),
        { expires: 5, path: '/' }
      );
    }
  }, [modalConfig]);

  useEffect((): (() => void) => {
    evaluateConditions();
    const intervalId = setInterval(evaluateConditions, CHECK_INTERVAL);
    return (): void => {
      clearInterval(intervalId);
    };
  }, [evaluateConditions]);

  return {
    ...modalConfig,
    triggerUpdate: evaluateConditions,
  };
};
